import { useAtomValue } from "jotai";

import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import AppTableList from "../../AppTableList/AppTableList";
import AppTable from "../../AppTable/AppTable";
import { IsMobileAtom } from "../../../App";
import GroupByTable from "./GroupByTable";

const DesktopTable = ({
  currentModule,
  simpleSearchQuery,
  query,
  setQuery,
  foundLayout,
  onColumnAdd,
  onColumnRemove,
  onColumnReorder,
  updateSortBy,
  updateQuery,
  selected,
  setSelected,
  updateAggregation,
  records,
  pagination,
  recordsLoading,
  error,
  errorMsg,
  onOpenRecordName,
  defaultRecordLayout,
  selectableRows,
  view,
  viewMode,
  columnAdd = true,
  defaultHiddenHeaders,
  draggingID,
  setDraggingID,
  dragDisabled,
  nameField,
  includeHeader,
  stretch = false,
  commentsWidget,
  defaultPrefill,
  isCustomTable = false,
  reusableViews,
  variableValues,
}: any) => {
  const updatedSelectableRows = !!currentModule?.Dynamic
    ? false
    : selectableRows;
  const updatedHiddenHeaders = !!currentModule?.Dynamic
    ? ["_RecordName"]
    : defaultHiddenHeaders;

  const openRecordName = currentModule?.Dynamic ? undefined : onOpenRecordName;

  if (query?.GetAllGrouped)
    return (
      <GroupByTable
        moduleAPIName={currentModule?.APIName}
        query={simpleSearchQuery || query}
        setQuery={setQuery}
        selectableRows={updatedSelectableRows}
        columnAdd={columnAdd}
        columnAddMessage={`Adding Field to ${
          foundLayout?.Name ?? "Default Layout"
        }`}
        onColumnAdd={onColumnAdd}
        onColumnRemove={onColumnRemove}
        onColumnReorder={onColumnReorder}
        onSort={updateSortBy}
        onUpdateQuery={updateQuery}
        selectedRecords={selected}
        setSelectedRecords={setSelected}
        useAggregations={view?.ShowAggregations}
        updateAggregation={updateAggregation}
        data={records}
        pagination={pagination}
        loading={recordsLoading}
        error={error}
        errorMsg={errorMsg}
        sticky={true}
        containerClasses={"pr-4"}
        onOpenRecordName={openRecordName}
        separateName
        paginationCSS="pr-6"
        group={query.GroupBy.Field}
        view={view}
        viewMode={viewMode}
        foundLayout={foundLayout}
        defaultHiddenHeaders={updatedHiddenHeaders}
        draggingID={draggingID}
        setDraggingID={setDraggingID}
        dragDisabled={dragDisabled}
        reusableViews={reusableViews}
        defaultPrefill={defaultPrefill}
      />
    );

  return (
    <AppTable
      moduleAPIName={currentModule?.APIName}
      query={simpleSearchQuery || query}
      setQuery={setQuery}
      selectableRows={!!updatedSelectableRows}
      columnAdd={columnAdd}
      columnAddMessage={`Adding Field to ${
        foundLayout?.Name ?? "Default Layout"
      }`}
      onColumnAdd={onColumnAdd}
      onColumnRemove={onColumnRemove}
      onColumnReorder={onColumnReorder}
      onSort={updateSortBy}
      onUpdateQuery={updateQuery}
      selectedRecords={selected}
      setSelectedRecords={setSelected}
      useAggregations={view?.ShowAggregations}
      updateAggregation={updateAggregation}
      data={records}
      pagination={pagination}
      loading={recordsLoading}
      error={error}
      errorMsg={errorMsg}
      sticky={true}
      containerClasses={"pr-4"}
      onOpenRecordName={openRecordName}
      separateName
      paginationCSS="pr-6"
      isDrawer={false}
      defaultRecordLayout={defaultRecordLayout}
      view={view}
      defaultHiddenHeaders={updatedHiddenHeaders}
      draggingID={draggingID}
      setDraggingID={setDraggingID}
      dragDisabled={dragDisabled}
      nameField={nameField}
      includeHeader={includeHeader}
      stretch={stretch}
      commentsWidget={commentsWidget}
      isCustomTable={isCustomTable}
      reusableViews={reusableViews}
      variableValues={variableValues}
    />
  );
};

const MobileTable = ({
  currentModule,
  simpleSearchQuery,
  query,
  setQuery,
  foundLayout,
  onColumnAdd,
  onColumnRemove,
  updateSortBy,
  updateQuery,
  selected,
  setSelected,
  records,
  pagination,
  recordsLoading,
  error,
  errorMsg,
  onOpenRecordName,
  toggleCreateRecordsDrawer,
  isSelection,
  setExportDrawer,
  loadTableInformation,
  toggleUpdateRecordsModal,
  toggleBulkApplyTags,
  toggleTemplateCSVModal,
  deleteSelectedRecords,
  toggleMergeDrawer,
  handleDuplicateRecord,
  selectedRecordsLength,
  selectableRows,
  view,
  nameField,
  columnAdd = true,
  includeHeader,
  reusableViews,
}: any) => {
  const updatedSelectableRows = !!currentModule?.Dynamic
    ? false
    : selectableRows;
  const updateOnOpenRecordName = !!currentModule?.Dynamic
    ? undefined
    : onOpenRecordName;

  return (
    <AppTableList
      moduleAPIName={currentModule?.APIName}
      query={simpleSearchQuery || query}
      setQuery={setQuery}
      selectableRows={updatedSelectableRows}
      onSort={updateSortBy}
      onUpdateQuery={updateQuery}
      columnAdd={columnAdd}
      columnAddMessage={`Adding Field to ${
        foundLayout?.Name ?? "Default Layout"
      }`}
      onColumnAdd={onColumnAdd}
      onColumnRemove={onColumnRemove}
      selectedRecords={selected}
      setSelectedRecords={setSelected}
      data={records}
      pagination={pagination}
      loading={recordsLoading}
      error={error}
      errorMsg={errorMsg}
      containerClasses={"pr-4"}
      toggleCreateRecordsDrawer={toggleCreateRecordsDrawer}
      onOpenRecordName={updateOnOpenRecordName}
      isSelection={isSelection}
      setExportDrawer={setExportDrawer}
      loadTableInformation={loadTableInformation}
      toggleUpdateRecordsModal={toggleUpdateRecordsModal}
      toggleBulkApplyTags={toggleBulkApplyTags}
      toggleTemplateCSVModal={toggleTemplateCSVModal}
      deleteSelectedRecords={deleteSelectedRecords}
      toggleMergeDrawer={toggleMergeDrawer}
      handleDuplicateRecord={handleDuplicateRecord}
      selectedRecordsLength={selectedRecordsLength}
      view={view}
      nameField={nameField}
      includeHeader={includeHeader}
      reusableViews={reusableViews}
    />
  );
};

const PageViewTables = (Props: any) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { cPermissions } = useCloudPermissions();
  const { pPermissions } = usePagePermissions(Props?.pageID);
  const { wPermissions } = useWorkspacePermissions();

  const dragDisabled =
    (!cPermissions.cloud_modify_page &&
      !wPermissions.workspace_modify_page &&
      !pPermissions.page_modify_view) ||
    Props?.dragDisabled;

  return mobileScreen && !Props?.query?.GetAllGrouped ? (
    <MobileTable {...Props} />
  ) : (
    <DesktopTable {...Props} dragDisabled={dragDisabled} />
  );
};

export default PageViewTables;
